import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { authService } from '../services/auth.service';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'user', 'sponsor', 'admin'
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    console.log('🔐 AuthProvider: Running checkAuth on mount');
    checkAuth();
  }, []);

  const checkAuth = async () => {
    console.log('🔐 checkAuth: Starting...');
    try {
      const token = localStorage.getItem('token');
      const storedUserType = localStorage.getItem('userType');
      const storedUser = localStorage.getItem('user');

      console.log('🔐 checkAuth: localStorage values:', {
        hasToken: !!token,
        userType: storedUserType,
        hasUser: !!storedUser
      });

      if (token && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        console.log('🔐 checkAuth: Parsed user:', parsedUser);
        console.log('🔐 checkAuth: User roleId:', parsedUser?.roleId);
        console.log('🔐 checkAuth: User activeRoleId:', parsedUser?.activeRoleId);
        
        setUser(parsedUser);
        setUserType(storedUserType);
        setIsAuthenticated(true);
        console.log('🔐 checkAuth: Auth state set to authenticated');
      } else {
        console.log('🔐 checkAuth: No token or user found, not authenticated');
      }
    } catch (error) {
      console.error('🔐 checkAuth: Error:', error);
      logout(false); // Don't show toast on auth check failure
    } finally {
      setLoading(false);
      console.log('🔐 checkAuth: Complete, loading set to false');
    }
  };

  const login = async (email, password, type = 'user') => {
    try {
      let response;
      
      if (type === 'user') {
        response = await authService.login({ email, password });
      } else if (type === 'sponsor') {
        response = await authService.sponsorLogin({ email, password });
      } else if (type === 'admin') {
        response = await authService.adminPanelLogin({ email, password });
      }

      // Server response format: { success, message, data: { user/sponsor/admin, token, refreshToken } }
      const responseData = response.data.data;
      const token = responseData.token;
      const userData = responseData.user || responseData.sponsor || responseData.admin;
      
      localStorage.setItem('token', token);
      localStorage.setItem('userType', type);
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (responseData.refreshToken) {
        localStorage.setItem('refreshToken', responseData.refreshToken);
      }
      
      setUser(userData);
      setUserType(type);
      setIsAuthenticated(true);
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async (showToast = true) => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setUserType(null);
      setIsAuthenticated(false);
      
      // Show logout success toaster (Bug #40)
      if (showToast) {
        toast.success('Logout Successful');
      }
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const hasRole = (roleId) => {
    if (!user) return false;
    return user.activeRoleId === roleId;
  };

  const value = {
    user,
    userType,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    hasRole,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
